import { m } from '@aimfora/i18n'
import { Button, cn } from '@aimfora/ui'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

const INTERVAL_MS = 7000

/** Placeholders, not statements from real teachers. Set to false once real quotes replace them. */
const QUOTES_ARE_EXAMPLES = true

const quotes = () => [
  {
    highlight: m.forside_quote_1_highlight(),
    text: m.forside_quote_1_text(),
    who: m.forside_quote_1_who(),
  },
  {
    highlight: m.forside_quote_2_highlight(),
    text: m.forside_quote_2_text(),
    who: m.forside_quote_2_who(),
  },
  {
    highlight: m.forside_quote_3_highlight(),
    text: m.forside_quote_3_text(),
    who: m.forside_quote_3_who(),
  },
  {
    highlight: m.forside_quote_4_highlight(),
    text: m.forside_quote_4_text(),
    who: m.forside_quote_4_who(),
  },
  {
    highlight: m.forside_quote_5_highlight(),
    text: m.forside_quote_5_text(),
    who: m.forside_quote_5_who(),
  },
]

/** Teacher quotes that rotate on their own. Pauses on hover and focus, and with reduced motion. */
export function QuoteCarousel() {
  const items = quotes()
  const total = items.length
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const rootRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!query) return
    setReducedMotion(query.matches)
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (paused || reducedMotion || total < 2) return
    const timer = setInterval(() => setIndex((value) => (value + 1) % total), INTERVAL_MS)
    return () => clearInterval(timer)
  }, [paused, reducedMotion, total])

  const step = (delta: number) => setIndex((value) => (value + delta + total) % total)

  return (
    <section
      ref={rootRef}
      className="relative"
      aria-roledescription={m.forside_carousel_label()}
      aria-label={m.forside_quotes_title()}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget)) setPaused(false)
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') step(-1)
        if (event.key === 'ArrowRight') step(1)
      }}
    >
      {/* All quotes sit on top of each other, so the height doesn't jump */}
      <div className="grid">
        {items.map((quote, i) => (
          <figure
            key={quote.highlight}
            className={cn(
              'transition-opacity duration-500 [grid-area:1/1]',
              i === index ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            aria-hidden={i !== index}
            aria-roledescription={m.forside_carousel_item()}
            aria-label={m.forside_carousel_position({ current: i + 1, total })}
          >
            <p className="font-semibold text-[13px] text-saffron-ink uppercase tracking-[0.08em]">
              {quote.highlight}
            </p>
            <blockquote className="mt-3 max-w-3xl font-medium text-[20px] leading-[1.35] tracking-[-0.01em] sm:text-[24px]">
              <span aria-hidden="true" className="text-saffron">
                “
              </span>
              {quote.text}
              <span aria-hidden="true" className="text-saffron">
                ”
              </span>
            </blockquote>
            <figcaption className="mt-4 text-ink-soft text-sm">{quote.who}</figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => step(-1)}
          aria-label={m.forside_carousel_previous()}
        >
          <ChevronLeftIcon />
        </Button>
        <nav className="flex gap-1.5" aria-label={m.forside_carousel_pick()}>
          {items.map((quote, i) => (
            <button
              key={quote.highlight}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={m.forside_carousel_go_to({ number: i + 1 })}
              aria-current={i === index ? 'true' : undefined}
              className={cn(
                'h-2 rounded-full transition-all',
                i === index ? 'w-6 bg-saffron' : 'w-2 bg-line-strong hover:bg-ink-soft',
              )}
            />
          ))}
        </nav>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => step(1)}
          aria-label={m.forside_carousel_next()}
        >
          <ChevronRightIcon />
        </Button>
        {QUOTES_ARE_EXAMPLES && (
          <span className="ml-auto text-[12px] text-ink-soft">
            {m.forside_quotes_placeholder()}
          </span>
        )}
      </div>
    </section>
  )
}
